/*
   Real-backend adapters for the /admin/users page.

   Two queries:
     - usersQuery(params) — GET /api/v1/users with page/page_size/search/sort/role/activity,
                            all applied server-side
     - apiKeysQuery()     — GET /api/v1/api-keys, for the Integrations page's key management

   Roles travel as the backend's own `admin | reviewer | sme | viewer | client`; display
   labels come from `ROLE_LABEL` in lib/roles, so there is no translation at the boundary.

   Mutations:
     - useCreateUserMutation   POST   /api/v1/users                  (invite or password)
     - useUpdateUserMutation   PATCH  /api/v1/users/{id}             (role, is_active, …)
     - useResendInviteMutation POST   /api/v1/users/{id}/resend-invite
     - useDeleteUserMutation   DELETE /api/v1/users/{id}

   Users carry only what the wire carries. There is no "last seen" (the backend records no
   logins) and no "bootstrap" flag (that was a guess by email address).

   Key fields the wire does not carry today are derived:
     - key.createdBy    → resolved from /users by created_by_id when possible
     - key.fromEnv      → true when created_by_id is null (env-provisioned)
*/

import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from "@tanstack/react-query"

import { http } from "@/lib/http"
import { fromPageWire, toQueryParams, type Page, type PageWire } from "@/lib/pagination"
import { qk } from "@/lib/query-keys"
import { Role, toRole } from "@/lib/roles"
import type { TableQueryParams } from "@/lib/table"

import {
  type ConsoleUser,
  type ConsoleUserId,
  type ServiceKey,
  type ServiceKeyId,
  type UserFilters,
} from "./users-keys-data"

// ── Wire types ────────────────────────────────────────────────────────

interface UserOutWire {
  id: number
  email: string
  full_name: string | null
  role: string
  is_active: boolean
  created_at: string
  invited_at: string | null
  accepted_at: string | null
}

interface ApiKeyOutWire {
  id: number
  name: string
  prefix: string
  created_by_id: number | null
  created_at: string
  revoked_at: string | null
  last_used_at?: string | null
}

// ── Role parsing ─────────────────────────────────────────────────────
// Unknown role → viewer: read-only, and never mistaken for a row-scoped client.

function fromWireRole(role: string): Role {
  return toRole(role) ?? Role.VIEWER
}

// ── Adapters ─────────────────────────────────────────────────────────

function toConsoleUser(u: UserOutWire): ConsoleUser {
  return {
    id: String(u.id) as ConsoleUserId,
    email: u.email,
    name: u.full_name,
    role: fromWireRole(u.role),
    createdAt: u.created_at,
    invitedAt: u.invited_at,
    acceptedAt: u.accepted_at,
    isActive: u.is_active,
  }
}

function toServiceKey(k: ApiKeyOutWire, usersById: Map<number, UserOutWire>): ServiceKey {
  const creator = k.created_by_id != null ? usersById.get(k.created_by_id) : null
  return {
    id: String(k.id) as ServiceKeyId,
    name: k.name,
    prefix: k.prefix,
    createdAt: k.created_at,
    createdBy: creator
      ? (creator.full_name ?? creator.email)
      : k.created_by_id != null
        ? `User #${k.created_by_id}`
        : "environment",
    lastUsedAt: k.last_used_at ?? null,
    fromEnv: k.created_by_id == null,
  }
}

// ── Users: paginated page + a small unpaginated map for key attribution ──

export interface UsersPageParams extends UserFilters {
  page: number
  pageSize: number
  search: string
  sort: { field: string; dir: "asc" | "desc" } | null
}

async function fetchUsersPage(
  params: UsersPageParams,
  signal: AbortSignal,
): Promise<Page<ConsoleUser>> {
  // Role and activity are both filtered by the backend, so the page it serves and its `total`
  // agree with what the table shows. Filtering a served page locally leaves holes in it and a
  // count that includes rows the reader cannot see.
  const extra: Record<string, string | undefined> = {}
  if (params.role) extra["role"] = params.role
  if (params.activity) extra["activity"] = params.activity
  const wire = await http.get<PageWire<UserOutWire>>("/api/v1/users", {
    params: toQueryParams(
      {
        page: params.page,
        pageSize: params.pageSize,
        sort: params.sort,
        filters: {},
        search: params.search,
      } satisfies TableQueryParams,
      extra,
    ),
    signal,
  })
  return fromPageWire(wire, toConsoleUser)
}

export const usersQuery = (params: UsersPageParams) =>
  queryOptions({
    queryKey: qk.users.list(params),
    queryFn: ({ signal }) => fetchUsersPage(params, signal),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })

async function fetchUsersByIdMap(signal: AbortSignal): Promise<Map<number, UserOutWire>> {
  const wire = await http.get<PageWire<UserOutWire>>("/api/v1/users", {
    params: { page_size: 100 },
    signal,
  })
  return new Map(wire.items.map((u) => [u.id, u]))
}

export const usersMapQuery = () =>
  queryOptions({
    queryKey: [...qk.users.all, "list", { form: "map" }] as const,
    queryFn: ({ signal }) => fetchUsersByIdMap(signal),
    staleTime: 60_000,
  })

// ── API keys: /api-keys is not yet paginated server-side. ────────────────

async function fetchApiKeys(qc: QueryClient, signal: AbortSignal): Promise<ServiceKey[]> {
  // Warm the users map from cache; single source of truth for name attribution.
  const usersById = await qc.ensureQueryData(usersMapQuery())
  const raw = await http.get<ApiKeyOutWire[]>("/api/v1/api-keys", { signal })
  return raw.filter((k) => k.revoked_at == null).map((k) => toServiceKey(k, usersById))
}

export const apiKeysQuery = (qc: QueryClient) =>
  queryOptions({
    queryKey: qk.apiKeys.list(),
    queryFn: ({ signal }) => fetchApiKeys(qc, signal),
    staleTime: 30_000,
  })

// ── Mutations ────────────────────────────────────────────────────────

export interface CreateUserInput {
  email: string
  fullName: string | null
  role: Role
  // Password path: caller sets one and shares it out of band. Invite path: server emails a link.
  mode: { kind: "password"; password: string } | { kind: "invite" }
}

interface CreateUserWire {
  email: string
  full_name: string | null
  role: Role
  password?: string
  send_invite?: boolean
}

async function createUserRequest(input: CreateUserInput): Promise<ConsoleUser> {
  const body: CreateUserWire = {
    email: input.email.trim(),
    full_name: input.fullName?.trim() || null,
    role: input.role,
  }
  if (input.mode.kind === "password") body.password = input.mode.password
  else body.send_invite = true
  const wire = await http.post<UserOutWire, CreateUserWire>("/api/v1/users", body)
  return toConsoleUser(wire)
}

export function useCreateUserMutation(): UseMutationResult<ConsoleUser, Error, CreateUserInput> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createUserRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.users.all })
    },
  })
}

export interface UpdateUserInput {
  id: ConsoleUserId
  body: { role?: Role; isActive?: boolean; fullName?: string; password?: string }
}

interface UpdateUserWire {
  role?: Role
  is_active?: boolean
  full_name?: string
  password?: string
}

async function updateUserRequest({ id, body }: UpdateUserInput): Promise<ConsoleUser> {
  // Only the fields being changed go on the wire; the backend treats a missing field as
  // "leave it alone", and an explicit null would mean something else.
  const wire: UpdateUserWire = {}
  if (body.role !== undefined) wire.role = body.role
  if (body.isActive !== undefined) wire.is_active = body.isActive
  if (body.fullName !== undefined) wire.full_name = body.fullName
  if (body.password !== undefined) wire.password = body.password
  const out = await http.patch<UserOutWire, UpdateUserWire>(`/api/v1/users/${id}`, wire)
  return toConsoleUser(out)
}

/**
 * Change a role, a name, a password, or whether the account may sign in at all.
 *
 * Deactivating, or setting a password, ends the person's live sessions on the backend.
 * Deactivating a pending invite also cancels the invite for good.
 */
export function useUpdateUserMutation(): UseMutationResult<ConsoleUser, Error, UpdateUserInput> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: updateUserRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.users.all })
      // Assignee names and the routing pickers on the queue come from the same accounts.
      void qc.invalidateQueries({ queryKey: qk.threads.all })
    },
  })
}

async function resendInviteRequest(userId: ConsoleUserId): Promise<void> {
  await http.post(`/api/v1/users/${userId}/resend-invite`)
}

export function useResendInviteMutation(): UseMutationResult<void, Error, ConsoleUserId> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: resendInviteRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.users.all })
    },
  })
}

async function deleteUserRequest(userId: ConsoleUserId): Promise<void> {
  await http.delete(`/api/v1/users/${userId}`)
}

export function useDeleteUserMutation(): UseMutationResult<void, Error, ConsoleUserId> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteUserRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.users.all })
      // A removed account also leaves any engagement it was a member of, as on develop.
      void qc.invalidateQueries({ queryKey: qk.clients.all })
    },
  })
}

// ── Service keys: create and revoke (admin) ─────────────────────────

export interface CreatedApiKey {
  name: string
  /** The full key. The backend returns it once and stores only its hash. */
  plaintext: string
}

export function useCreateApiKeyMutation(): UseMutationResult<CreatedApiKey, Error, string> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (name: string) => {
      const created = await http.post<ApiKeyOutWire & { api_key: string }>("/api/v1/api-keys", {
        name,
      })
      return { name: created.name, plaintext: created.api_key }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.apiKeys.all })
    },
  })
}

export function useRevokeApiKeyMutation(): UseMutationResult<void, Error, ServiceKeyId> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (keyId: ServiceKeyId) => {
      await http.post(`/api/v1/api-keys/${keyId}/revoke`)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.apiKeys.all })
    },
  })
}
